import React from 'react'
import { useWavesurfer } from '@wavesurfer/react'
import { useLatest } from 'ahooks'

import type { WaveSurferOptions } from 'wavesurfer.js'


import { useGlobalAudioPlayer } from '@/hooks/audio/store';
import { useRef, useMemo, useImperativeHandle, useEffect } from 'react';
import { tw } from 'tagged-classnames-free';
export interface WaveformAction {
  getCurrentTime: () => number
}

export interface WaveformProps extends React.HTMLAttributes<HTMLDivElement> {
  audio?: Pick<
    WaveSurferOptions,
    'peaks' | 'duration' | 'waveColor' | 'progressColor'
  > & {
    url?: string | null
    height?: number
  }
  maxPeaks?: number
  // unit: seconds
  onPlayAudio?: (time: number) => void
  loading?: boolean
  showWave?: boolean
}

const defaultPeaks = [
  [0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0],
]
const defaultDuration = 50

const Waveform = (props: WaveformProps, ref: React.Ref<WaveformAction>) => {
  const {
    audio,
    onPlayAudio,
    className,
    loading,
    maxPeaks,
    showWave,
    ...rest
  } = props

  const globalPlayer = useGlobalAudioPlayer()

  const containerRef = useRef<HTMLDivElement>(null)
  const onPlayAudioLatest = useLatest(onPlayAudio)
  const isMatchGlobalSrc = globalPlayer.matchSrc(audio?.url)

  // 避免 wavesurfer 实例反复重建
  const memoPeaks = useMemo(() => {
    if (audio?.peaks?.length) {
      if (maxPeaks && audio.peaks[0].length > maxPeaks) {
        const sample = audio.peaks[0].length / maxPeaks

        return audio.peaks?.map((item) => {
          return Array.from({ length: maxPeaks })
            .fill(null)
            .map((_, index) => {
              return item[Math.ceil(index * sample)]
            })
        })
      } else {
        return audio?.peaks
      }
    }
    return defaultPeaks
  }, [audio?.peaks, maxPeaks])

  const { waveColor, progressColor, progress } = useMemo(() => {
    return {
      waveColor:
        showWave === false ? 'transparent' : audio?.waveColor ?? '#3f3f3f',
      progressColor:
        showWave === false ? 'transparent' : audio?.progressColor ?? '#828282',
      progress: audio
        ? (globalPlayer.currentTimeInSeconds / (audio?.duration || 0)) * 100
        : 0,
    }
  }, [audio, globalPlayer.currentTimeInSeconds, showWave])

  const { wavesurfer, isReady, isPlaying, currentTime } = useWavesurfer({
    container: containerRef,
    waveColor:
      showWave === false ? 'transparent' : audio?.waveColor ?? '#3f3f3f',
    progressColor:
      showWave === false ? 'transparent' : audio?.progressColor ?? '#828282',
    cursorWidth: 0,
    height: audio?.height ?? 80,
    peaks: memoPeaks,
    duration: loading ? defaultDuration : audio?.duration,
    interact: !loading,
  })

  useImperativeHandle(ref, () => {
    return {
      getCurrentTime: () => {
        return wavesurfer?.getCurrentTime() || 0
      },
    }
  })

  useEffect(() => {
    if (!wavesurfer) {
      return
    }

    const handleInteraction = (time: number) => {
      wavesurfer.setTime(time)
      setTimeout(() => {
        onPlayAudioLatest.current?.(time)
      })
    }

    wavesurfer.on('interaction', handleInteraction)

    return () => {
      wavesurfer.un('interaction', handleInteraction)
    }
  }, [onPlayAudioLatest, wavesurfer])

  useEffect(() => {
    if (!wavesurfer) {
      return
    }

    if (isMatchGlobalSrc) {
      wavesurfer.setTime(globalPlayer.currentTimeInSeconds)
    }
  }, [isMatchGlobalSrc, globalPlayer.currentTimeInSeconds, wavesurfer])

  const handleClick = (e: any) => {
    e.stopPropagation()
    if (wavesurfer && isReady && showWave === false) {
      const rect = containerRef.current?.getBoundingClientRect()
      if (rect) {
        const progress = (e.clientX - rect.left) / rect.width
        wavesurfer.seekTo(progress)
      }
    }
  }

  return (
    <div
      // 新建层叠上下文，屏蔽内部 shadow-root 节点的 z-index 影响
      className={tw`relative z-0 cursor-pointer ${className} ${showWave === false ? 'rounded-[10px]' : ''}`}
      {...rest}
      ref={containerRef}
      style={
        showWave === false
          ? {
              background: `linear-gradient(to right, var(--f-other-4) ${progress}%, var(--f-border-secondary) ${progress}%)`,
            }
          : undefined
      }
      onClick={handleClick}
    />
  )
}

const ForwardRefWaveform = React.forwardRef(Waveform)

export default ForwardRefWaveform
